/**
 * The machine drawer's Autonomy section: session control for this machine (the switch and how
 * long a permission request waits for a person), and its ceiling, which only someone on that
 * computer can change: each entry with the exact command that allows it there.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { Machine, NodeTrustInfo } from '@orgus/protocol';
import { CopyButton, Select, Tooltip, toUiError } from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { ControlSwitch, controlBlocked } from '../control/ControlSwitch.tsx';
import { setControl, useControlState } from '../control/live.ts';
import { WAIT_OPTIONS, machineSummary, waitLabel } from '../control/model.ts';
import { CeilingHint } from '../trust/CeilingHint.tsx';
import { allowCommand, capabilityLabel } from '../trust/ceilingHint.ts';

function Fact({ label, note, children, aside }: { label: string; note?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className="flex min-w-0 items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-ui text-fg">{children}</span>
          {note && <span className="text-caption text-fg-subtle">{note}</span>}
        </span>
        {aside}
      </dd>
    </div>
  );
}

export function MachineAutonomy({ machine, trust }: { machine: Machine; trust: NodeTrustInfo | null }) {
  const control = useControlState();
  const m = control?.machines[machine.id];
  const canChange = useCan('admin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summary = machineSummary(m, machine.online);
  const blocked = controlBlocked({ canChange, online: machine.online, name: machine.name, m });
  const deny = trust?.ceilings ?? null;

  const changeWait = async (v: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await setControl(machine.id, true, Number(v));
      if (!r.ok) throw new Error(r.message ?? 'The machine did not change the wait.');
    } catch (e) {
      const u = toUiError(e);
      setError([u.what, u.why, u.next].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Autonomy" className="flex flex-col gap-4">
      <h3 className="flex min-h-8 items-center text-ui font-medium text-fg">Autonomy</h3>
      <dl className="flex flex-col gap-4">
        <Fact label="Session control" note={summary.detail} aside={m ? <ControlSwitch machineId={machine.id} name={machine.name} online={machine.online} m={m} /> : undefined}>
          {summary.label}
        </Fact>
        {m?.installed && (
          <Fact
            label="Permission wait"
            note="Then Claude Code asks in the session"
            aside={
              <Tooltip content={blocked ?? undefined}>
                <span tabIndex={blocked ? 0 : -1} className="inline-flex">
                  <Select
                    size="sm"
                    aria-label={`How long a permission request on ${machine.name} waits for a person in Orgus`}
                    wrapperClassName="w-36"
                    options={WAIT_OPTIONS.some((o) => Number(o.value) === m.waitSeconds) ? WAIT_OPTIONS : [...WAIT_OPTIONS, { value: String(m.waitSeconds), label: waitLabel(m.waitSeconds) }]}
                    value={String(m.waitSeconds)}
                    disabled={!!blocked || busy}
                    onValueChange={(v) => void changeWait(v)}
                  />
                </span>
              </Tooltip>
            }
          >
            {waitLabel(m.waitSeconds)} for a person
          </Fact>
        )}
        {error && (
          <p role="alert" className="text-caption text-status-danger-ink">
            {error}
          </p>
        )}
        <Fact label="Ceiling" note={deny === null ? 'Its agent has not reported one yet' : trust?.ceilingDefault ? 'The default: no ceiling file on that computer' : 'Set on that computer'}>
          {deny === null ? 'Not reported' : deny.length ? `Refuses ${deny.length === 1 ? 'one kind' : `${deny.length} kinds`} of work from the hub` : 'Refuses nothing beyond Orgus policy'}
        </Fact>
      </dl>
      {deny && deny.length > 0 && (
        <ul className="-mx-2 -mt-2 flex flex-col" aria-label={`What ${machine.name} refuses`}>
          {deny.map((c) => (
            <li key={c} className="flex h-row min-w-0 items-center gap-3 rounded-control px-2 hover:bg-hover">
              <span className="min-w-0 flex-1 truncate text-ui text-fg-muted">{capabilityLabel(c)}</span>
              <span className="hidden min-w-0 shrink truncate font-mono text-caption text-fg-subtle sm:inline">{c}</span>
              <CopyButton text={allowCommand(c)} label={`Copy ${allowCommand(c)}`} />
            </li>
          ))}
        </ul>
      )}
      {deny !== null && <CeilingHint lead="The console cannot raise it. On that computer, see or change it:" command="orgus agent ceiling" />}
    </section>
  );
}
